import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useForm } from "react-hook-form";
import {
  Building2,
  ShieldCheck,
  MapPin,
  Phone,
  Mail,
  ArrowUpRight,
  CheckCircle2,
  Award,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SitePhotosCarousel } from "@/components/SitePhotosCarousel";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import {
  API,
  createTranslator,
  stats,
  services,
  sectors,
  projects,
  credentials,
  clients,
  leadership,
  careerRoles,
} from "@/lib/constants";

export default function MainPage({ language }) {
  const t = createTranslator(language);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { name: "", email: "", phone: "", organization: "", message: "" },
  });

  useEffect(() => {
    axios
      .post(`${API}/analytics/pageview`, { path: window.location.pathname })
      .catch(() => {});
  }, []);

  const onSubmit = async (data) => {
    setSubmitStatus("loading");
    try {
      await axios.post(`${API}/contact`, data);
      setSubmitStatus("success");
      reset();
    } catch (error) {
      console.error(error);
      setSubmitStatus("error");
    }
  };

  return (
    <div className="App">
      <Helmet>
        <title>
          Infraglobal Engineering Pvt. Ltd. | EPC, Electrical, Civil &amp; Mechanical Engineering
          Company India
        </title>
        <meta
          name="description"
          content="Infraglobal Engineering Pvt. Ltd. (IGEL) provides EPC, Electrical, Mechanical, Civil Engineering, O&amp;M, Infrastructure Development, Airport Infrastructure, Industrial Projects, Material Supply and Project Management services across India."
        />
      </Helmet>
      <Header language={language} />
      <main className="pt-24 content-justify" data-testid="main-content">
        {/* ── Hero ── */}
        <section
          id="hero"
          className="relative min-h-screen flex items-center text-white"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(0,51,102,0.9), rgba(0,51,102,0.4)), url('https://customer-assets.emergentagent.com/job_infra-engineering-1/artifacts/54ael69r_infragloabal%20distribution.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          data-testid="hero-section"
        >
          <div className="absolute inset-0 hero-glow" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <p
              className="text-sm uppercase tracking-[0.4em] text-orange-200 section-subtitle"
              data-testid="hero-eyebrow"
            >
              {t("hero.eyebrow", "ENGINEERING INFRASTRUCTURE FOR GLOBAL GROWTH")}
            </p>
            <h1
              className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl uppercase leading-tight mt-6 break-words"
              data-testid="hero-title"
            >
              {t("hero.title", "Infraglobal Engineering")}
              <span className="block text-orange-300">
                {t("hero.subtitle", "EPC | Material Supply | O&M")}
              </span>
            </h1>
            <p
              className="max-w-2xl mt-6 text-lg md:text-xl text-slate-100"
              data-testid="hero-description"
            >
              {t(
                "hero.description",
                "A trusted Indian EPC partner delivering complex infrastructure, energy, and industrial projects for government bodies, PSUs, and private sector."
              )}
            </p>
            <div className="flex flex-wrap gap-4 mt-6" data-testid="hero-cta-group">
              <Button
                className="bg-[#FF9933] text-[#003366] hover:bg-[#ffb05c] rounded-none px-8 py-4 font-bold uppercase tracking-wider whitespace-normal text-center"
                asChild
                data-testid="hero-primary-cta"
              >
                <a href="#contact" data-testid="hero-primary-cta-link">
                  {t("hero.ctaPrimary", "Request Company Profile")}
                </a>
              </Button>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#003366] rounded-none px-8 py-4 font-bold uppercase tracking-wider whitespace-normal text-center"
                asChild
                data-testid="hero-secondary-cta"
              >
                <a href="#projects" data-testid="hero-secondary-cta-link">
                  {t("hero.ctaSecondary", "View Execution Exposure")}
                </a>
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6" data-testid="hero-stats">
              {stats.map((stat) => (
                <div
                  key={stat.testid}
                  className="border border-white/20 bg-white/10 p-6"
                  data-testid={stat.testid}
                >
                  <p className="font-heading text-4xl" data-testid={`${stat.testid}-value`}>
                    {stat.value}
                  </p>
                  <p
                    className="text-sm uppercase tracking-wider"
                    data-testid={`${stat.testid}-label`}
                  >
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── About ── */}
        <section id="about" className="py-20 bg-white" data-testid="about-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p
                className="text-sm uppercase tracking-[0.4em] text-orange-500"
                data-testid="about-eyebrow"
              >
                {t("about.eyebrow", "Who We Are")}
              </p>
              <h2
                className="font-heading text-4xl md:text-5xl uppercase mt-4 text-[#003366]"
                data-testid="about-title"
              >
                {t("about.title", "Nation-Building Infrastructure Partner")}
              </h2>
              <p className="mt-6 text-lg text-slate-600" data-testid="about-description">
                {t(
                  "about.description",
                  "Infraglobal Engineering Private Limited is a multidisciplinary engineering, EPC, and materials supply organization involved in infrastructure works and the supply of electrical, mechanical, electromechanical, and industrial consumables, including chemicals, for projects in the energy, transportation, and industrial domains. Its operations emphasize compliance-driven execution and governance practices consistent with institutional and public-sector procurement frameworks."
                )}
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3" data-testid="about-highlight-1">
                  <CheckCircle2 className="text-orange-500" />
                  <div>
                    <p
                      className="font-semibold text-slate-800"
                      data-testid="about-highlight-1-title"
                    >
                      {t("about.highlight1.title", "Tender-Ready Documentation")}
                    </p>
                    <p className="text-sm text-slate-600" data-testid="about-highlight-1-text">
                      {t(
                        "about.highlight1.text",
                        "Prequalification dossiers, compliance registers, and statutory clearances."
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="about-highlight-2">
                  <Award className="text-orange-500" />
                  <div>
                    <p
                      className="font-semibold text-slate-800"
                      data-testid="about-highlight-2-title"
                    >
                      {t(
                        "about.highlight2.title",
                        "Execution Capability & Quality Framework"
                      )}
                    </p>
                    <p className="text-sm text-slate-600" data-testid="about-highlight-2-text">
                      {t(
                        "about.highlight2.text",
                        "Multidisciplinary teams deployed under documented QA/QC procedures and schedule control systems."
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative" data-testid="about-image-wrapper">
              <img
                loading="lazy"
                src="https://customer-assets.emergentagent.com/job_infra-engineering-1/artifacts/6mz1r7vl_1%20%281%29.png"
                alt="Infraglobal engineering team on site"
                className="w-full h-[480px] object-cover"
                data-testid="about-image"
              />
              <div
                className="absolute -bottom-6 -left-6 bg-[#003366] text-white p-6"
                data-testid="about-image-card"
              >
                <p
                  className="text-sm uppercase tracking-widest"
                  data-testid="about-image-card-label"
                >
                  {t("about.headquarters", "Headquarters")}
                </p>
                <p className="text-xl font-heading" data-testid="about-image-card-value">
                  {t("about.headquartersValue", "GHAZIABAD UTTAR PRADESH, INDIA")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <SitePhotosCarousel />

        {/* ── Services ── */}
        <section id="services" className="py-20 bg-[#F5F7FA]" data-testid="services-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <p
                  className="text-sm uppercase tracking-[0.4em] text-orange-500"
                  data-testid="services-eyebrow"
                >
                  {t("services.eyebrow", "Core Services")}
                </p>
                <h2
                  className="font-heading text-4xl md:text-5xl uppercase mt-4 text-[#003366]"
                  data-testid="services-title"
                >
                  {t("services.title", "EPC Capabilities Built for Government Standards")}
                </h2>
              </div>
              <p className="max-w-md text-slate-600" data-testid="services-description">
                {t(
                  "services.description",
                  "Integrated engineering and procurement support aligned with safety requirements, statutory compliance, and clear documentation practices for government and institutional tenders."
                )}
              </p>
            </div>
            <div
              className="mt-6 grid grid-cols-1 md:grid-cols-3 border border-gray-200"
              data-testid="services-grid"
            >
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={service.testid}
                    className={`border-b border-gray-200 p-6 bg-white hover:bg-gray-50 transition-colors ${
                      index % 3 !== 2 ? "md:border-r" : ""
                    } ${index < 3 ? "md:border-b" : "md:border-b-0"}`}
                    data-testid={service.testid}
                  >
                    <Icon
                      className="text-orange-500"
                      data-testid={`${service.testid}-icon`}
                    />
                    <h3
                      className="font-heading text-2xl mt-4 text-[#003366]"
                      data-testid={`${service.testid}-title`}
                    >
                      {service.title}
                    </h3>
                    <p
                      className="mt-3 text-slate-600"
                      data-testid={`${service.testid}-text`}
                    >
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Sectors ── */}
        <section id="sectors" className="py-20 bg-white" data-testid="sectors-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <p
                  className="text-sm uppercase tracking-[0.4em] text-orange-500"
                  data-testid="sectors-eyebrow"
                >
                  {t("sectors.eyebrow", "Sectors Served")}
                </p>
                <h2
                  className="font-heading text-4xl md:text-5xl uppercase mt-4 text-[#003366]"
                  data-testid="sectors-title"
                >
                  {t("sectors.title", "Engineering Services Across Identified Sectors")}
                </h2>
              </div>
              <div
                className="flex items-center gap-3 text-slate-600"
                data-testid="sectors-note"
              >
                <Users className="text-orange-500" />
                <p data-testid="sectors-note-text">
                  {t(
                    "sectors.description",
                    "Capabilities structured to support projects undertaken by government bodies, PSUs, and state agencies."
                  )}
                </p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {sectors.map((sector) => (
                <div
                  key={sector.testid}
                  className="border border-slate-200 p-6 hover:border-orange-400 transition-colors"
                  data-testid={sector.testid}
                >
                  <h3
                    className="font-heading text-2xl text-[#003366]"
                    data-testid={`${sector.testid}-title`}
                  >
                    {sector.title}
                  </h3>
                  <p
                    className="mt-3 text-slate-600"
                    data-testid={`${sector.testid}-text`}
                  >
                    {sector.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Projects ── */}
        <section
          id="projects"
          className="py-20 bg-[#0F172A] text-white"
          data-testid="projects-section"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <p
                  className="text-sm uppercase tracking-[0.4em] text-orange-300"
                  data-testid="projects-eyebrow"
                >
                  {t("projects.eyebrow", "Execution Exposure & Project Readiness")}
                </p>
                <h2
                  className="font-heading text-4xl md:text-5xl uppercase mt-4"
                  data-testid="projects-title"
                >
                  {t(
                    "projects.title",
                    "Indicative project profiles aligned with EPC, SUPPLY, and O&M requirements"
                  )}
                </h2>
              </div>
              <div
                className="flex items-center gap-3 text-orange-200"
                data-testid="projects-note"
              >
                <ArrowUpRight />
                <p data-testid="projects-note-text">
                  {t("projects.description", "Representative portfolio for tender submissions.")}
                </p>
              </div>
            </div>
            <div
              className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-4"
              data-testid="projects-grid"
            >
              {projects.map((project) => (
                <div
                  key={project.testid}
                  className={`group relative overflow-hidden h-[360px] ${project.span}`}
                  data-testid={project.testid}
                >
                  <img
                    loading="lazy"
                    src={project.image}
                    alt={project.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid={`${project.testid}-image`}
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10"
                    aria-hidden="true"
                  />
                  <div className="relative h-full flex flex-col justify-end p-6">
                    <p
                      className="text-orange-200 text-xs uppercase tracking-widest"
                      data-testid={`${project.testid}-subtitle`}
                    >
                      {project.subtitle}
                    </p>
                    <h3
                      className="font-heading text-3xl mt-2"
                      data-testid={`${project.testid}-title`}
                    >
                      {project.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Credentials ── */}
        <section
          id="credentials"
          className="py-20 bg-[#F5F7FA]"
          data-testid="credentials-section"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p
                className="text-sm uppercase tracking-[0.4em] text-orange-500"
                data-testid="credentials-eyebrow"
              >
                {t("credentials.eyebrow", "Credentials")}
              </p>
              <h2
                className="font-heading text-4xl md:text-5xl uppercase mt-4 text-[#003366]"
                data-testid="credentials-title"
              >
                {t("credentials.title", "Compliance That Strengthens Evaluation")}
              </h2>
              <p className="mt-6 text-slate-600" data-testid="credentials-description">
                {t(
                  "credentials.description",
                  "Our statutory and organizational compliances are structured to meet public-sector procurement norms and PSU pre-qualification (PQ) requirements, enabling transparent participation across tender stages."
                )}
              </p>
              <div className="mt-8 space-y-4" data-testid="credentials-list">
                {credentials.map((item, index) => (
                  <div
                    key={item.text}
                    className="flex items-center gap-3"
                    data-testid={`credential-item-${index}`}
                  >
                    <ShieldCheck className="text-orange-500" />
                    <p className="text-slate-700" data-testid={`credential-item-${index}-text`}>
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div
              className="bg-white border border-slate-200 p-6"
              data-testid="credentials-card"
            >
              <h3
                className="font-heading text-3xl text-[#003366]"
                data-testid="credentials-card-title"
              >
                {t("credentials.cardTitle", "Safety & Quality Assurance")}
              </h3>
              <p className="mt-4 text-slate-600" data-testid="credentials-card-text">
                {t(
                  "credentials.cardDescription",
                  "The organization adopts safety-first practices and quality control measures aligned with statutory norms and client specifications. Emphasis is placed on preventive safety measures, workmanship quality, and compliance monitoring during project execution."
                )}
              </p>
              <ul
                className="mt-6 space-y-3 text-slate-600"
                data-testid="credentials-card-list"
              >
                <li data-testid="credentials-card-item-1">
                  {t(
                    "credentials.point1",
                    "Safety-driven execution and hazard prevention practices."
                  )}
                </li>
                <li data-testid="credentials-card-item-2">
                  {t(
                    "credentials.point2",
                    "Quality checks as per approved drawings and specifications."
                  )}
                </li>
                <li data-testid="credentials-card-item-3">
                  {t(
                    "credentials.point3",
                    "Periodic reporting to clients and stakeholders."
                  )}
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── Governance ── */}
        <section id="governance" className="py-20 bg-white" data-testid="governance-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <p
                  className="text-sm uppercase tracking-[0.4em] text-orange-500"
                  data-testid="governance-eyebrow"
                >
                  {t("governance.eyebrow", "Governance")}
                </p>
                <h2
                  className="font-heading text-4xl md:text-5xl uppercase mt-4 text-[#003366]"
                  data-testid="governance-title"
                >
                  {t("governance.title", "Professionally Managed Governance Structure")}
                </h2>
              </div>
              <div
                className="flex items-center gap-3 text-slate-600"
                data-testid="governance-note"
              >
                <Building2 className="text-orange-500" />
                <p data-testid="governance-note-text">
                  {t(
                    "governance.description",
                    "Engineering, Material Supply, and O&M Expertise."
                  )}
                </p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {leadership.map((leader) => {
                const Icon = leader.icon;
                return (
                  <div
                    key={leader.testid}
                    className="border border-slate-200 p-6 flex flex-col sm:flex-row gap-4"
                    data-testid={leader.testid}
                  >
                    <div
                      className="w-32 h-32 bg-[#F5F7FA] flex items-center justify-center"
                      data-testid={`${leader.testid}-icon-wrapper`}
                    >
                      <Icon
                        className="text-orange-500"
                        size={48}
                        data-testid={`${leader.testid}-icon`}
                      />
                    </div>
                    <div>
                      <p
                        className="font-heading text-2xl text-[#003366]"
                        data-testid={`${leader.testid}-name`}
                      >
                        {leader.name}
                      </p>
                      <p
                        className="text-orange-500 uppercase tracking-wider text-sm"
                        data-testid={`${leader.testid}-role`}
                      >
                        {leader.role}
                      </p>
                      <p className="mt-4 text-slate-600" data-testid={`${leader.testid}-bio`}>
                        {leader.bio}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Careers ── */}
        <section id="careers" className="py-20 bg-[#F5F7FA]" data-testid="careers-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <p
                  className="text-sm uppercase tracking-[0.4em] text-orange-500"
                  data-testid="careers-eyebrow"
                >
                  {t("careers.eyebrow", "Careers")}
                </p>
                <h2
                  className="font-heading text-4xl md:text-5xl uppercase mt-4 text-[#003366]"
                  data-testid="careers-title"
                >
                  {t("careers.title", "Build Infrastructure With Us")}
                </h2>
              </div>
              <p className="max-w-md text-slate-600" data-testid="careers-description">
                {t(
                  "careers.description",
                  "We welcome engineers, project managers, and site teams who thrive in tender-ready execution environments."
                )}
              </p>
            </div>
            <div
              className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4"
              data-testid="careers-grid"
            >
              {careerRoles.map((career) => (
                <div
                  key={career.testid}
                  className="border border-slate-200 bg-white p-6"
                  data-testid={career.testid}
                >
                  <img
                    loading="lazy"
                    src={career.image}
                    alt={career.title}
                    className="w-full h-40 object-cover mb-4"
                    data-testid={`${career.testid}-image`}
                  />
                  <h3
                    className="font-heading text-2xl text-[#003366]"
                    data-testid={`${career.testid}-title`}
                  >
                    {career.title}
                  </h3>
                  <p
                    className="mt-3 text-slate-600"
                    data-testid={`${career.testid}-text`}
                  >
                    {career.text}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-4" data-testid="careers-cta-group">
              <Button
                className="bg-[#003366] text-white hover:bg-[#002244] rounded-none px-8 py-4 font-bold uppercase tracking-wider"
                asChild
                data-testid="careers-primary-cta"
              >
                <a href="/careers?type=apply" data-testid="careers-primary-cta-link">
                  {t("careers.ctaPrimary", "Apply With Your Profile")}
                </a>
              </Button>
              <Button
                variant="outline"
                className="border-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white rounded-none px-8 py-4 font-bold uppercase tracking-wider"
                asChild
                data-testid="careers-secondary-cta"
              >
                <a href="/careers?type=share" data-testid="careers-secondary-cta-link">
                  {t("careers.ctaSecondary", "Share Resume & Credentials")}
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* ── Clients ── */}
        <section id="clients" className="py-20 bg-[#F5F7FA]" data-testid="clients-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <p
                  className="text-sm uppercase tracking-[0.4em] text-orange-500"
                  data-testid="clients-eyebrow"
                >
                  {t("clients.eyebrow", "Public & Private Sector Engagement")}
                </p>
                <h2
                  className="font-heading text-4xl md:text-5xl uppercase mt-4 text-[#003366]"
                  data-testid="clients-title"
                >
                  {t("clients.title", "Institutional Engagement & Sector Exposure")}
                </h2>
              </div>
              <p className="max-w-md text-slate-600" data-testid="clients-description">
                {t(
                  "clients.description",
                  "The company is structured to support and engage with institutional and public-sector organizations across energy, infrastructure, and transportation programs, in line with applicable procurement frameworks."
                )}
              </p>
            </div>
            <div
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
              data-testid="clients-grid"
            >
              {clients.map((client, index) => (
                <div
                  key={client.label}
                  className="border border-slate-200 bg-white p-4 text-sm uppercase tracking-widest text-slate-600"
                  data-testid={`client-item-${index}`}
                >
                  {client.label}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contact ── */}
        <section
          id="contact"
          className="py-20 bg-[#003366] text-white"
          data-testid="contact-section"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p
                className="text-sm uppercase tracking-[0.4em] text-orange-300"
                data-testid="contact-eyebrow"
              >
                {t("contact.eyebrow", "Contact")}
              </p>
              <h2
                className="font-heading text-4xl md:text-5xl uppercase mt-4"
                data-testid="contact-title"
              >
                {t("contact.title", "Connect with Our Team")}
              </h2>
              <p className="mt-6 text-slate-100" data-testid="contact-description">
                {t(
                  "contact.description",
                  "For tender requirements, supply of materials, or project-related enquiries, please contact us."
                )}
              </p>
              <div className="mt-6 space-y-4" data-testid="contact-details">
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <MapPin className="text-orange-300" />
                  <div>
                    <p
                      className="text-sm uppercase tracking-widest"
                      data-testid="contact-address-label"
                    >
                      {t("contact.addressLabel", "Registered Office")}
                    </p>
                    <p className="text-lg" data-testid="contact-address-text">
                      Shakti Khand-III, Indirapuram, Ghaziabad, Uttar Pradesh - 201014 (INDIA)
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <Phone className="text-orange-300" />
                  <div>
                    <p
                      className="text-sm uppercase tracking-widest"
                      data-testid="contact-phone-label"
                    >
                      {t("contact.phoneLabel", "Phone")}
                    </p>
                    <p className="text-lg break-words" data-testid="contact-phone-text">
                      +91 93108 92512, +91 88606 94272
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <Mail className="text-orange-300" />
                  <div>
                    <p
                      className="text-sm uppercase tracking-widest"
                      data-testid="contact-email-label"
                    >
                      {t("contact.emailLabel", "Email")}
                    </p>
                    <p className="text-lg break-words" data-testid="contact-email-text">
                      <span className="block">info@infraglobalengineering.com</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white text-slate-800 p-6" data-testid="contact-form-card">
              <h3
                className="font-heading text-3xl text-[#003366]"
                data-testid="contact-form-title"
              >
                {t("contact.formTitle", "Request Documentation")}
              </h3>
              <p className="mt-3 text-slate-600" data-testid="contact-form-description">
                {t(
                  "contact.formDescription",
                  "Provide your details to receive the company profile, compliance pack, and project capability statement."
                )}
              </p>
              <form
                className="mt-6 space-y-4"
                onSubmit={handleSubmit(onSubmit)}
                data-testid="contact-form"
              >
                <div>
                  <label className="text-sm font-semibold" data-testid="contact-name-label">
                    {t("contact.nameLabel", "Full Name")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t("contact.namePlaceholder", "Enter your full name")}
                    {...register("name", {
                      required: t("form.nameRequired", "Name is required"),
                    })}
                    data-testid="contact-name-input"
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500 mt-1" data-testid="contact-name-error">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="contact-email-label">
                    {t("contact.emailLabelForm", "Work Email")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t("contact.emailPlaceholder", "Enter your work email")}
                    {...register("email", {
                      required: t("form.emailRequired", "Email is required"),
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: t("form.emailInvalid", "Enter a valid email"),
                      },
                    })}
                    data-testid="contact-email-input"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500 mt-1" data-testid="contact-email-error">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="contact-phone-label">
                    {t("contact.phoneLabelForm", "Phone Number")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t("contact.phonePlaceholder", "Enter your phone number")}
                    {...register("phone", {
                      required: t("form.phoneRequired", "Phone number is required"),
                    })}
                    data-testid="contact-phone-input"
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-500 mt-1" data-testid="contact-phone-error">
                      {errors.phone.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="contact-org-label">
                    {t("contact.organizationLabel", "Organization")}
                  </label>
                  <Input
                    className="mt-2 rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t(
                      "contact.organizationPlaceholder",
                      "Organization / Department"
                    )}
                    {...register("organization", {
                      required: t("form.organizationRequired", "Organization is required"),
                    })}
                    data-testid="contact-org-input"
                  />
                  {errors.organization && (
                    <p className="text-sm text-red-500 mt-1" data-testid="contact-org-error">
                      {errors.organization.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold" data-testid="contact-message-label">
                    {t("contact.messageLabel", "Message")}
                  </label>
                  <Textarea
                    className="mt-2 min-h-[120px] rounded-none border-gray-300 focus:border-[#003366] focus:ring-1 focus:ring-[#003366] bg-gray-50"
                    placeholder={t(
                      "contact.messagePlaceholder",
                      "Share your tender or project requirements"
                    )}
                    {...register("message", {
                      required: t("form.messageRequired", "Message is required"),
                    })}
                    data-testid="contact-message-input"
                  />
                  {errors.message && (
                    <p
                      className="text-sm text-red-500 mt-1"
                      data-testid="contact-message-error"
                    >
                      {errors.message.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#003366] text-white hover:bg-[#002244] rounded-none px-8 py-4 font-bold uppercase tracking-wider"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                >
                  {isSubmitting
                    ? t("careersPage.submitting", "Submitting...")
                    : t("contact.submit", "Submit Request")}
                </Button>
                {submitStatus === "success" && (
                  <p
                    className="text-green-600 text-sm"
                    data-testid="contact-success-message"
                  >
                    {t(
                      "contact.success",
                      "Thank you. Our team will reach out with the requested documentation."
                    )}
                  </p>
                )}
                {submitStatus === "error" && (
                  <p className="text-red-600 text-sm" data-testid="contact-error-message">
                    {t(
                      "contact.error",
                      "Submission failed. Please try again or email tenders@infraglobaleng.com."
                    )}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer language={language} />
      <WhatsAppButton />
    </div>
  );
}
